"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TriangleAlert as AlertTriangle, SquareCheck as CheckSquare, Wrench } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
const checklistItems = [{
  num: "01",
  title: "1. Confirm the exact machine and included equipment."
}, {
  num: "02",
  title: "2. Review the documentation supplied for the model."
}, {
  num: "03",
  title: "3. Measure the complete operating area."
}, {
  num: "04",
  title: "4. Confirm access for bringing the equipment into the location."
}, {
  num: "05",
  title: "5. Verify the product-specific power requirements."
}, {
  num: "06",
  title: "6. Confirm the controller and cables supplied."
}, {
  num: "07",
  title: "7. Check the inflatable and blower requirements."
}, {
  num: "08",
  title: "8. Position the equipment according to its instructions."
}, {
  num: "09",
  title: "9. Review visible condition before operation."
}, {
  num: "10",
  title: "10. Understand normal and emergency controls."
}, {
  num: "11",
  title: "11. Confirm the applicable rider and operating rules."
}, {
  num: "12",
  title: "12. Keep product documentation available for future setup and maintenance."
}];
export default function SetupChecklist() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1],
        staggerChildren: shouldReduceMotion ? 0 : 0.04
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 8
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.25,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="mechanicalbullsetupchecklist" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Area */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={containerVariants} className="space-y-4 mb-10">
          <div className="flex items-center gap-3">
            <span className="h-2 w-2 bg-primary rounded-none" />
            <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              PRE-FLIGHT &amp; INSTALLATION PROTOCOL
            </p>
          </div>

          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold uppercase tracking-tight text-foreground">
              Mechanical Bull Setup Checklist
            </h2>
            <div className="flex items-center gap-2 font-mono text-xs text-muted-foreground uppercase border border-border px-3 py-1.5 bg-muted/30 w-fit rounded-none">
              <Wrench className="w-3.5 h-3.5 text-primary" />
              <span>Standard Field Inspection Spec</span>
            </div>
          </div>

          <Separator className="bg-border" />
        </motion.div>

        {/* 12-Item Checklist Grid */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.1
      }} variants={containerVariants} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {checklistItems.map((item, i) => <motion.div key={item.num} data-index={i} variants={itemVariants} className="group relative bg-card text-card-foreground border border-border rounded-none p-5 shadow-xs transition-[border-color,background-color] duration-200 hover:border-primary/80 hover:bg-card flex flex-col justify-between min-h-[120px]">
              {/* Technical datum header */}
              <div className="flex items-center justify-between pb-3 mb-2 border-b border-border/60">
                <Badge variant="outline" className="rounded-none border-border font-mono text-xs tracking-wider text-muted-foreground bg-muted/40 px-2 py-0.5 group-hover:border-primary/50 group-hover:text-primary transition-colors duration-200">
                  STEP // {item.num}
                </Badge>
                <CheckSquare className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors duration-200" />
              </div>

              {/* Exact Copy Specification */}
              <p className="font-default text-sm sm:text-base font-medium leading-snug text-foreground">
                {item.title}
              </p>

              {/* Bottom datum line indicator */}
              <div className="mt-4 pt-2 flex items-center justify-between font-mono text-[10px] text-muted-foreground/60 border-t border-dashed border-border/50">
                <span>INDEX REF: MBS-0{item.num}</span>
                <span className="uppercase tracking-widest">VERIFIED</span>
              </div>
            </motion.div>)}
        </motion.div>

        {/* Industrial Caution Notice Plate */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={containerVariants} className="mt-8 border border-border bg-muted/40 p-5 sm:p-6 rounded-none relative overflow-hidden">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="p-2.5 bg-primary/10 border border-primary/20 text-primary shrink-0 rounded-none">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div className="space-y-1">
              <span className="font-mono text-xs uppercase tracking-wider text-primary font-semibold block">
                MANDATORY OPERATIONAL NOTICE
              </span>
              <p className="text-sm font-default text-foreground leading-relaxed font-medium">
                Use this checklist as a planning tool, not as a replacement for the instructions supplied with the exact mechanical bull.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}